import { Link, Navigate } from 'react-router-dom';
import { reportTypesFor } from '@ni/shared';
import { useOrg } from '../../api/app-context';
import { loadErrorLine } from '../../api/errors';
import { useReports } from '../../api/report-hooks';
import { IfWrites } from '../../components/write-access';
import { formatDate, formatDay } from '../../lib/format';
import { REPORTS_TITLE, STATUS_LABELS, peopleLine, sortReports } from '../../lib/report-copy';
import { LOADING } from '../../lib/copy';
import './reports.css';

/**
 * **Reports** (docs/notes/2026-09-22-reports-design.md §2): what the office has
 * asked the system to prepare. Upcoming events first; archived reports are not
 * listed.
 */
export function Reports() {
  const org = useOrg();
  const types = reportTypesFor(org.vertical);
  const list = useReports();
  if (types.length === 0) return <Navigate to="/" replace />;

  // The office's own calendar day, not UTC's: an event tonight is still upcoming.
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const items = sortReports(
    (list.data?.items ?? []).filter((item) => item.state === 'active'),
    today,
  );

  return (
    <div className="column">
      <div className="head">
        <div className="titles">
          <div className="title">{REPORTS_TITLE}</div>
        </div>
        <div className="actions-right">
          <IfWrites>
            <Link className="btn hero" to="/reports/new">
              New report
            </Link>
          </IfWrites>
        </div>
      </div>

      {list.isError ? <p role="alert">{loadErrorLine(list.error)}</p> : null}
      {list.isPending ? <p>{LOADING}</p> : null}
      {list.data && items.length === 0 ? (
        <p className="report-list-empty">No reports yet.</p>
      ) : null}

      <ul className="report-list">
        {items.map((item) => (
          <li key={item.id} className="report-row">
            <Link className="report-row-title" to={`/reports/${item.id}`}>
              {item.title}
            </Link>
            <div className="report-row-meta">
              {[
                types.find((t) => t.key === item.type)?.display_name,
                item.for_date ? formatDate(item.for_date) : null,
                peopleLine(item.people),
              ]
                .filter(Boolean)
                .join(' · ')}
            </div>
            {/* A report that was not prepared is the one word in copper: it
                needs the office. The others are quiet. */}
            <div className="report-row-status">
              <span className={`report-status is-${item.status}`}>
                {STATUS_LABELS[item.status]}
              </span>
              {item.last_version_at ? (
                <span className="report-row-date">{formatDay(item.last_version_at)}</span>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
